import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useNotificationStore,
  type NotificationPrefs,
} from '../stores/notifications'

type NotificationType = keyof NotificationPrefs

/**
 * Hook that exposes a `notify` function for firing browser (push) notifications.
 *
 * Guards:
 *  - Returns early when the Notification API is unavailable.
 *  - Returns early when permission is not 'granted'.
 *  - Returns early when the user has disabled the given notification type.
 *  - For types other than 'inbound_call', skips when the tab is already visible.
 */
export function useBrowserNotifications() {
  const navigate = useNavigate()
  const { permission, prefs, setPref, requestPermission } =
    useNotificationStore()

  const notify = useCallback(
    (
      type: NotificationType,
      title: string,
      body: string,
      navigateTo?: string,
    ) => {
      // Guard: browser support
      if (typeof window === 'undefined' || !('Notification' in window)) {
        return
      }

      // Guard: permission
      if (permission !== 'granted') {
        return
      }

      // Guard: user preference
      if (!prefs[type]) {
        return
      }

      // For everything except inbound calls, skip if the user is already
      // looking at the app -- no need to interrupt with a system notification.
      if (type !== 'inbound_call' && document.visibilityState === 'visible') {
        return
      }

      const notification = new Notification(title, {
        body,
        icon: '/favicon.ico',
        tag: type,
      })

      notification.onclick = () => {
        window.focus()
        if (navigateTo) {
          navigate(navigateTo)
        }
        notification.close()
      }
    },
    [permission, prefs, navigate],
  )

  return { notify, permission, requestPermission, prefs, setPref }
}
